import React, { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';

import { getLayoutDetail } from '@/api/analytics';
import { AnnotatableTimeChart } from '../chart/AnnotatableTimeChart';
import type {
  ChartSeries,
  ChartPhoto,
  ChartSegment,
  ChartKeypoint,
  Domain,
} from '../chart/types';

const PHOTO_COUNT = 40; // ~1 per 15min over a working day — enough to consult without zoom

// Per-shelf line colors — a small qualitative palette, cycled by shelf index.
const SHELF_COLORS = ['#f59e0b', '#6366f1', '#22c55e', '#ec4899', '#0ea5e9', '#a855f7', '#ef4444', '#14b8a6'];

/** Shared fetch — same SWR key as `RackChart` uses internally, so a caller that
 *  only needs e.g. `working_hours` (to place the F2 open/close keypoints)
 *  dedupes against the chart's own request instead of firing a second one. */
export function useRackLayoutDetail(zoneId: string, day: string, rackId: number | undefined) {
  return useSWR(
    zoneId && rackId != null ? ['rv-interactive-rack', zoneId, day, rackId] : null,
    () =>
      // item 16: 5-min buckets (was 15-min) — the finest the shelf-timeseries
      // endpoint offers (§ DateBin5Min, services/services/api/analytics.py);
      // real ShelfOccupancy rows land every ~1.77 min, so 5-min still
      // averages several real samples per bucket instead of interpolating.
      getLayoutDetail(zoneId, day, day, rackId, '5min', {
        photoMode: 'even',
        photoCount: PHOTO_COUNT,
      }).then((r) => r.data),
    // No focus-revalidation — a refetch on tab refocus reflows/re-fetches
    // every rack chart at once, which read as the whole panel "blinking"
    // (item 12). Same fix already applied to DailyReportView for the same
    // reason.
    { revalidateOnFocus: false },
  );
}

/** One BE auto-detected shortage window of a rack, in chart units. */
export interface AutoSeg extends ChartSegment {
  missingPct: number;
}

/** item A (2026-09): seeds a rack's segments from its BE auto-detected
 *  windows (`auto_segments` — the SAME per-shelf shortage intervals the
 *  report text was built from) once, on first load — invisible, one
 *  instance per rack (its own `useRackLayoutDetail` call dedupes via SWR
 *  against the rack's own `RackChart`, no extra request). Deliberately does
 *  NOT push the aggregate back into `body_html` on seed — the report text
 *  already reflects the server's own aggregate math (which isn't
 *  byte-identical to the blocks' simplified re-derivation); only a real
 *  user edit should touch the text. */
export function AutoSegSeeder({
  zoneId,
  day,
  rackId,
  alreadySeeded,
  onSeed,
}: {
  zoneId: string;
  day: string;
  rackId: number;
  alreadySeeded: boolean;
  onSeed: (rackId: number, segs: AutoSeg[]) => void;
}) {
  const { data } = useRackLayoutDetail(zoneId, day, rackId);
  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current || alreadySeeded || !data) return;
    doneRef.current = true;
    const auto = data.auto_segments ?? [];
    if (!auto.length) return;
    onSeed(
      rackId,
      auto.map((seg, i) => ({
        id: `auto-${rackId}-${i}-${seg.t_start}`,
        t_start: new Date(seg.t_start).getTime(),
        t_end: new Date(seg.t_end).getTime(),
        missingPct: seg.missing_pct,
      })),
    );
  }, [data, alreadySeeded, rackId, onSeed]);
  return null;
}

/**
 * Fetches one rack's fill-% series (averaged across its shelves) + photos and
 * renders it as an `AnnotatableTimeChart`. Pure fetch+series-shaping wrapper —
 * all interaction state (segments/keypoints/readout) is owned by the caller
 * (`FoodStatusChartBlock` / `KeypointChartBlock`) so racks of the same type can
 * share state (F2 keypoints are synced across racks) or not (F1 segments aren't).
 */
export function RackChart({
  zoneId,
  day,
  rackId,
  rackName,
  annotateCreates,
  segments,
  keypoints,
  selectedId,
  onSelect,
  onSegmentCreate,
  onSegmentChange,
  onKeypointChange,
  readout,
  addSegmentLabel,
}: {
  zoneId: string;
  day: string;
  rackId: number;
  rackName: string;
  annotateCreates: 'segment' | 'none';
  segments?: ChartSegment[];
  keypoints?: ChartKeypoint[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** `missingPct` is pre-bucketed (10..100) from this rack's own fill-series. */
  onSegmentCreate?: (seg: ChartSegment, missingPct: number) => void;
  onSegmentChange?: (seg: ChartSegment, missingPct: number) => void;
  onKeypointChange?: (kp: ChartKeypoint, valueAtT: number | null) => void;
  readout?: React.ReactNode;
  /** Desktop explicit "Добавить проблему" button label (see AnnotatableTimeChart). */
  addSegmentLabel?: string;
}) {
  const { data, isLoading } = useRackLayoutDetail(zoneId, day, rackId);

  const domainFull = useMemo<Domain | null>(() => {
    const wh = data?.working_hours;
    if (!wh) return null;
    const s = new Date(wh.start).getTime();
    const e = new Date(wh.end).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e > s ? [s, e] : null;
  }, [data?.working_hours]);

  // Rack fill% = average across its shelves at each point (§3 LayoutRack.fill_series)
  // — used for segment/keypoint value math. The CHART itself shows the
  // per-shelf breakdown below (Alexey: the averaged single line hides which
  // shelf is actually empty).
  const fillPoints = useMemo(() => {
    const ts = data?.shelf_timeseries;
    if (!ts) return [];
    return ts.points.map((p) => {
      const vals = Object.values(p.values).filter((v): v is number => v != null);
      const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      return { t: new Date(p.date).getTime(), v: avg };
    });
  }, [data?.shelf_timeseries]);

  // item 4: STACKED area, one band per shelf — matches the "normal"
  // (конструктор-mode) shelf chart elsewhere in the app instead of separate
  // line traces. Shelves share one timestamp axis already (`ts.points[i]
  // .values` is keyed by shelf id), so this stacks cleanly: each shelf's
  // baseline is the cumulative top of every shelf below it (fixed order,
  // null treated as 0 contribution — a shelf with no reading that instant
  // just doesn't add to the stack rather than punching a hole in it).
  //
  // The stack is NORMALISED: every band is the shelf's fill ÷ shelf count,
  // so the stacked top is the rack average and never exceeds the fixed
  // 0–100% axis (Alexey 2026-09-03/08: raw per-shelf values summed to
  // N×100% and the upper shelves ran out of the plot). Same reading as the
  // on-page ShelfTimeseriesChart, which scales its axis to N×100 and labels
  // ticks ÷N — here the axis stays [0,100] (keypoint `level` ticks are
  // drawn against it) so the data is scaled instead. `valueScale` restores
  // the shelf's own % in the tooltip. Values are also clamped to 0..100 in
  // case a bucket ever reads past the calibrated ceiling.
  const shelfSeries: ChartSeries[] = useMemo(() => {
    const ts = data?.shelf_timeseries;
    if (!ts) return [];
    const times = ts.points.map((p) => new Date(p.date).getTime());
    const cumulative = ts.points.map(() => 0);
    const n = Math.max(ts.shelves.length, 1);
    return ts.shelves.map((shelf, i) => {
      const baseline = cumulative.map((v, pi) => ({ t: times[pi], v }));
      const points = ts.points.map((p, pi) => {
        const v = Math.max(0, Math.min(100, p.values[shelf.id] ?? 0));
        cumulative[pi] += v / n;
        return { t: times[pi], v: cumulative[pi] };
      });
      return {
        id: `shelf-${shelf.id}`,
        kind: 'area' as const,
        axis: 'left' as const,
        color: SHELF_COLORS[i % SHELF_COLORS.length],
        label: shelf.name,
        points,
        baseline,
        valueScale: n,
      };
    });
  }, [data?.shelf_timeseries]);

  const photos: ChartPhoto[] = useMemo(
    () =>
      (data?.rack_photos ?? [])
        .filter((p) => p.image_url)
        .map((p) => ({
          t: new Date(p.at).getTime(),
          url: p.image_url!,
          caption: p.fill_pct != null ? `${p.fill_pct}%` : undefined,
        })),
    [data?.rack_photos],
  );

  const handleKeypointChange = onKeypointChange
    ? (kp: ChartKeypoint) => {
        const inRange = fillPoints.filter((p) => Math.abs(p.t - kp.t) <= 15 * 60 * 1000);
        const v = inRange.length
          ? inRange.reduce((a, p) => a + p.v, 0) / inRange.length
          : null;
        onKeypointChange(kp, v);
      }
    : undefined;
  const handleSegmentCreate = onSegmentCreate
    ? (seg: ChartSegment) => onSegmentCreate(seg, bucketMissingPct(fillPoints, seg.t_start, seg.t_end))
    : undefined;
  const handleSegmentChange = onSegmentChange
    ? (seg: ChartSegment) => onSegmentChange(seg, bucketMissingPct(fillPoints, seg.t_start, seg.t_end))
    : undefined;

  if (!domainFull) {
    return <div className="h-56 animate-pulse rounded-xl bg-muted" />;
  }

  return (
    <AnnotatableTimeChart
      title={rackName}
      series={shelfSeries}
      photos={photos}
      domainFull={domainFull}
      height={220}
      // item 14: Y axis always reads a fixed 0-100% scale (a stacked series'
      // cumulative top can exceed 100) — each series' OWN tooltip value is
      // un-stacked separately inside the chart (via `ChartSeries.baseline`).
      yDomain={[0, 100]}
      valueUnit="%"
      annotateCreates={annotateCreates}
      segments={segments}
      keypoints={keypoints}
      selectedId={selectedId}
      onSelect={onSelect}
      onSegmentCreate={handleSegmentCreate}
      onSegmentChange={handleSegmentChange}
      onKeypointChange={handleKeypointChange}
      readout={isLoading ? undefined : readout}
      addSegmentLabel={addSegmentLabel}
    />
  );
}

/** Average of a fill-series between t0/t1 (falls back to the nearest point). */
export function avgInRange(points: { t: number; v: number }[], t0: number, t1: number): number {
  const inRange = points.filter((p) => p.t >= t0 && p.t <= t1);
  if (inRange.length) return inRange.reduce((a, p) => a + p.v, 0) / inRange.length;
  const mid = (t0 + t1) / 2;
  let best: { t: number; v: number } | null = null;
  for (const p of points) {
    if (!best || Math.abs(p.t - mid) < Math.abs(best.t - mid)) best = p;
  }
  return best?.v ?? 0;
}

/** %-missing bucketed to a 10..100 step (F1 prefill), from average fill% in range. */
export function bucketMissingPct(points: { t: number; v: number }[], t0: number, t1: number): number {
  const missing = Math.max(0, Math.min(100, 100 - avgInRange(points, t0, t1)));
  return Math.max(10, Math.min(100, Math.round(missing / 10) * 10));
}
